import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, platformLabel } from "../lib/api";
import { Search, X, ChevronDown } from "lucide-react";

const CATEGORIES = [
  { v: "", l: "All Categories" },
  { v: "consoles", l: "Consoles" },
  { v: "games", l: "Games" },
  { v: "accessories", l: "Accessories" },
  { v: "controllers", l: "Controllers" },
  { v: "digital", l: "Digital" },
];

const PLATFORMS = [
  { v: "", l: "All Platforms" },
  { v: "ps5", l: "PS5" },
  { v: "ps4", l: "PS4" },
  { v: "switch 2", l: "Switch 2" },
  { v: "switch 1", l: "Switch 1" },
  { v: "xbox", l: "Xbox" },
  { v: "pc", l: "PC" },
  { v: "multi", l: "Multi-Platform" },
];

const SORT_OPTIONS = [
  { v: "", l: "Default" },
  { v: "price_low", l: "Price: Low → High" },
  { v: "price_high", l: "Price: High → Low" },
  { v: "name_asc", l: "Name: A → Z" },
];

function Select({ value, onChange, options }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none bg-navy-800 border border-navy-600 text-slate-200 text-sm rounded px-3 py-2 pr-8 focus:outline-none focus:border-gold/60 cursor-pointer"
      >
        {options.map((o) => (
          <option key={o.v} value={o.v}>{o.l}</option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
    </div>
  );
}

export default function InventoryPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(params.get("q") || "");

  const category = params.get("category") || "";
  const platform = params.get("platform") || "";
  const sort = params.get("sort") || "";
  const q = params.get("q") || "";

  const setParam = useCallback((key, val) => {
    const np = new URLSearchParams(params);
    if (val) np.set(key, val); else np.delete(key);
    np.delete("page");
    setParams(np);
  }, [params, setParams]);

  const clearAll = () => setParams({});

  const hasFilters = category || platform || sort || q;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/products/inventory", {
          params: {
            category: category || undefined,
            platform: platform || undefined,
            sort: sort || undefined,
            q: q || undefined,
          },
        });
        const data = r.data;
        const items = data.items ?? (Array.isArray(data) ? data : []);
        setProducts(items);
        setTotal(data.total ?? items.length);
      } catch {
        setProducts([]);
        setTotal(0);
      }
      setLoading(false);
    })();
  }, [category, platform, sort, q]);

  const handleSearch = useCallback(
    (e) => {
      e.preventDefault();
      setParam("q", searchInput.trim());
    },
    [searchInput, setParam]
  );

  const categoryLabel = (v) => CATEGORIES.find((c) => c.v === v)?.l || v;

  return (
    <>
      <Helmet>
        <title>Inventory | GamesTrade</title>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-black tracking-tight text-white">Inventory</h1>
          <p className="text-slate-400 text-sm mt-1">Browse all available products — filter by category or platform.</p>
        </div>

        {/* Filters bar */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          {/* Search */}
          <form onSubmit={handleSearch} className="relative flex items-center">
            <Search className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search products…"
              className="bg-navy-800 border border-navy-600 text-slate-200 text-sm rounded pl-9 pr-3 py-2 w-52 focus:outline-none focus:border-gold/60 placeholder-slate-500"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => { setSearchInput(""); setParam("q", ""); }}
                className="absolute right-2 text-slate-400 hover:text-white"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </form>

          <Select value={category} onChange={(v) => setParam("category", v)} options={CATEGORIES} />
          <Select value={platform} onChange={(v) => setParam("platform", v)} options={PLATFORMS} />
          <Select value={sort} onChange={(v) => setParam("sort", v)} options={SORT_OPTIONS} />

          {hasFilters && (
            <button
              onClick={clearAll}
              className="flex items-center gap-1 text-xs text-slate-400 hover:text-white border border-navy-600 rounded px-2.5 py-2 transition"
            >
              <X className="w-3 h-3" /> Clear all
            </button>
          )}

          <span className="ml-auto text-xs text-slate-500">
            {loading ? "Loading…" : `${total} item${total !== 1 ? "s" : ""}`}
          </span>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-slate-500 text-sm py-20 text-center">Loading inventory…</div>
        ) : products.length === 0 ? (
          <div className="text-slate-500 text-sm py-20 text-center">No products match your filters.</div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-navy-700">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-navy-800 text-left text-xs uppercase tracking-widest text-slate-400 border-b border-navy-700">
                  <th className="px-4 py-3 font-semibold w-[35%]">Product</th>
                  <th className="px-4 py-3 font-semibold">Category</th>
                  <th className="px-4 py-3 font-semibold">Platform</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold text-right">Price</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy-700/60">
                {products.map((p) => (
                  <tr
                    key={p.id}
                    onClick={() => navigate(`/product/${p.id}`)}
                    className="bg-navy-900 hover:bg-navy-800/70 transition-colors cursor-pointer"
                  >
                    {/* Product name */}
                    <td className="px-4 py-3 font-medium text-slate-200">{p.name}</td>

                    {/* Category */}
                    <td className="px-4 py-3 text-slate-400 capitalize">{categoryLabel(p.category)}</td>

                    {/* Platform */}
                    <td className="px-4 py-3 text-slate-400">{platformLabel(p.platform)}</td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      {p.is_preorder ? (
                        <span className="inline-flex items-center text-[10px] font-black uppercase tracking-widest bg-gold/20 text-gold border border-gold/30 px-1.5 py-0.5 rounded">
                          Pre-Order
                        </span>
                      ) : p.stock > 0 ? (
                        <span className="text-xs font-semibold text-emerald-400">In Stock</span>
                      ) : (
                        <span className="text-xs font-semibold text-rose-400">Out of Stock</span>
                      )}
                    </td>

                    {/* Price */}
                    <td className="px-4 py-3 text-right">
                      <span className="font-black text-gold">₹{p.price.toLocaleString()}</span>
                      {p.original_price && p.original_price > p.price && (
                        <span className="ml-2 text-xs text-slate-500 line-through">₹{p.original_price.toLocaleString()}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </div>
    </>
  );
}
